import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import Table from '../common/Table';
import AssignComplaintModal from './AssignComplaintModal';
import apiClient from '../../api/apiClient';
import { toastError } from '../common/toast';
import tenantStorage from '../../utils/tenantStorage';
import { formatDateToDDMMYYYY } from '../../utils/dateHelper';

const UnassignedComplaints = ({ loadData = false }) => {
  const navigate = useNavigate();
  
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [assignMode, setAssignMode] = useState('assign');
  const userRole = tenantStorage.getItem('userRole');

  useEffect(() => {
    if (loadData) {
      fetchUnassignedComplaints();
    }
  }, [loadData]);

  const fetchUnassignedComplaints = async () => {
    setLoading(true);
    try {
      // Check if user is staff to add mode=staff parameter
      const isStaff = userRole === 'RT_Staff';
      const params = new URLSearchParams();
      
      if (isStaff) {
        params.append('mode', 'staff');
      }
      
      const url = params.toString() ? `/complaints/unassigned?${params.toString()}` : '/complaints/unassigned';
      const response = await apiClient.get(url);
      setComplaints(response.data || []);
    } catch (error) {
      console.error('Error fetching unassigned complaints:', error);
      toastError('Failed to fetch unassigned complaints');
      setComplaints([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusClass = status.toLowerCase().replace(/_/g, '-');
    return (
      <span className={`complaint-status status-${statusClass}`}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  const getPriorityBadge = (priority) => {
    return (
      <span className={`complaint-priority priority-${priority.toLowerCase()}`}>
        {priority}
      </span>
    );
  };

  const handleAssignComplaint = (complaint) => {
    setSelectedComplaint(complaint);
    setAssignMode('assign');
    setAssignModalOpen(true);
  };

  const handleAssignToMe = (complaint) => {
    setSelectedComplaint(complaint);
    setAssignMode('assignSelf');
    setAssignModalOpen(true);
  };

  const handleAssignSuccess = () => {
    // Refresh the complaints list
    fetchUnassignedComplaints();
  };

  const columns = [
    {
      Header: 'Complaint ID',
      accessor: 'complaint_id',
      Cell: ({ value }) => `#${value}`
    },
    {
      Header: 'Date',
      accessor: 'created_at',
      Cell: ({ value }) => formatDateToDDMMYYYY(value)
    },
    {
      Header: 'PG Name',
      accessor: 'pg_name',
      Cell: ({ value, row }) => {
        if (userRole === 'RT_Staff') {
          return value;
        }
        return (
          <RTButton
            type="link"
            onClick={() => window.open(`/paying-guest/view/${row.pg_id}`, '_blank')}
            className="rt-link-button"
          >
            {value}
          </RTButton>
        );
      }
    },
    {
      Header: 'Building',
      accessor: 'building_name',
      Cell: ({ value, row }) => {
        if (userRole === 'RT_Staff') {
          return value;
        }
        return (
          <RTButton
            type="link"
            onClick={() => window.open(`/building/view/${row.building_id}`, '_blank')}
            className="rt-link-button"
          >
            {value}
          </RTButton>
        );
      }
    },
    {
      Header: 'Type',
      accessor: 'complaint_type',
      Cell: ({ value }) => {
        // Handle both string and object types
        if (typeof value === 'object' && value !== null) {
          return value.type_name || value.name || 'Unknown';
        }
        return value || 'Unknown';
      }
    },
    {
      Header: 'Priority',
      accessor: 'priority',
      Cell: ({ value }) => getPriorityBadge(value)
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          {userRole === 'RT_Staff' && (
            <RTButton
              type="link"
              permissionKey="complaints.add"
              onClick={() => handleAssignToMe(row)}
              style={{ fontWeight: 'bold'}}
            >
              Assign To Me
            </RTButton>
          )}
          <RTButton
            type="link"
            onClick={() => navigate(`/complaints/view/${row.complaint_id}`)}
            style={{ fontWeight: 'bold' }}
          >
            View
          </RTButton>
          {userRole !== 'RT_Staff' && (
            <RTButton
              type="link"
              permissionKey="complaints.add"
              onClick={() => handleAssignComplaint(row)}
            >
              Assign Complaint
            </RTButton>
          )}
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
        <RTSpinner size="medium" showMessage={true} message="Loading unassigned complaints..." />
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 0' }}>
      {!loadData ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: '#666',
          fontSize: '16px'
        }}>
          Click on this tab to load unassigned complaints
        </div>
      ) : (
        <Table
          columns={columns}
          data={complaints}
          loading={loading}
          exportable={true}
          showSearch={true}
          showPagination={true}
          showTotalCount={true}
          totalCountText="Total Unassigned Complaints:"
        />
      )}
      
      <AssignComplaintModal
        isOpen={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        complaint={selectedComplaint}
        mode={assignMode}
        onSuccess={handleAssignSuccess}
      />
    </div>
  );
};

export default UnassignedComplaints;
